// src/components/FilterBar.jsx
import React from "react";
import styled from "styled-components";
import { FiSearch, FiX } from "react-icons/fi";
import { inCategory, productCode, productAltCode } from "../services/db";
import { useLang } from "../context/LanguageContext";

/**
 * Search + category + sort. Controlled by the parent so both the homepage
 * section and the full products page can share one filter UI.
 */
export default function FilterBar({
  search,
  onSearch,
  categories = [],
  categoryId,
  onCategory,
  sort,
  onSort,
  resultCount,
  showSort = true,
}) {
  const { t } = useLang();

  return (
    <Wrap>
      <SearchField>
        <FiSearch />
        <input
          type="search"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder={t("filters.searchPlaceholder")}
          aria-label={t("filters.searchLabel")}
        />
        {search && (
          <Clear
            onClick={() => onSearch("")}
            aria-label={t("filters.clearSearch")}
          >
            <FiX />
          </Clear>
        )}
      </SearchField>

      <Chips role="group" aria-label={t("filters.categoryGroup")}>
        <Chip $active={!categoryId} onClick={() => onCategory("")}>
          {t("filters.all")}
        </Chip>
        {categories.map((c) => (
          <Chip
            key={c.id}
            $active={categoryId === c.id}
            onClick={() => onCategory(c.id)}
          >
            {c.nameSq}
          </Chip>
        ))}
      </Chips>

      <Meta>
        {typeof resultCount === "number" && (
          <Count>
            {resultCount}{" "}
            {resultCount === 1 ? t("common.product") : t("common.products")}
          </Count>
        )}

        {showSort && (
          <Select
            value={sort}
            onChange={(e) => onSort(e.target.value)}
            aria-label={t("filters.sortLabel")}
          >
            <option value="code">{t("filters.sortCode")}</option>
            <option value="name">{t("filters.sortName")}</option>
            <option value="price-asc">{t("filters.sortPriceAsc")}</option>
            <option value="price-desc">{t("filters.sortPriceDesc")}</option>
          </Select>
        )}
      </Meta>
    </Wrap>
  );
}

/**
 * Shared filtering + sorting so the homepage and products page
 * can never drift apart in behaviour.
 */
export const applyFilters = (products, { search, categoryId, sort }) => {
  const q = search.trim().toLowerCase();

  let list = products.filter((p) => {
    if (!inCategory(p, categoryId)) return false;
    if (!q) return true;
    return [p.nameSq, p.nameDe, productCode(p), productAltCode(p)]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(q));
  });

  const by = {
    // uncoded products sort to the end rather than clustering at the top
    code: (a, b) => {
      const ac = productCode(a);
      const bc = productCode(b);
      if (!ac && !bc)
        return String(a.nameSq || a.nameDe || "").localeCompare(
          String(b.nameSq || b.nameDe || ""),
          "sq",
        );
      if (!ac) return 1;
      if (!bc) return -1;
      return ac.localeCompare(bc, "sq", { numeric: true });
    },
    name: (a, b) => String(a.nameSq).localeCompare(String(b.nameSq), "sq"),
    "price-asc": (a, b) => a.price - b.price,
    "price-desc": (a, b) => b.price - a.price,
  };

  return list.sort(by[sort] || by.code);
};

/* ------------------------------------------------------------------ */

const Wrap = styled.div`
  display: grid;
  grid-template-columns: minmax(240px, 380px) 1fr auto;
  align-items: center;
  gap: 20px 28px;

  ${({ theme }) => theme.down.lg} {
    grid-template-columns: 1fr;
  }
`;

const SearchField = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  svg:first-child {
    position: absolute;
    left: 16px;
    color: ${({ theme }) => theme.color.muted};
    pointer-events: none;
  }

  input {
    width: 100%;
    height: 48px;
    padding: 0 42px 0 44px;
    border: 1px solid ${({ theme }) => theme.color.line};
    border-radius: ${({ theme }) => theme.radius.pill};
    background: ${({ theme }) => theme.color.bg};
    font-size: ${({ theme }) => theme.size.sm};
    transition:
      border-color 0.25s,
      box-shadow 0.25s;

    &::placeholder {
      color: ${({ theme }) => theme.color.muted};
    }
    &:focus {
      outline: none;
      border-color: ${({ theme }) => theme.color.brand};
      box-shadow: 0 0 0 3px ${({ theme }) => theme.color.brandSoft};
    }
    &::-webkit-search-cancel-button {
      display: none;
    }
  }
`;

const Clear = styled.button`
  position: absolute;
  right: 14px;
  display: grid;
  place-items: center;
  color: ${({ theme }) => theme.color.muted};

  &:hover {
    color: ${({ theme }) => theme.color.ink};
  }
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  ${({ theme }) => theme.down.lg} {
    overflow-x: auto;
    flex-wrap: nowrap;
    padding-bottom: 4px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const Chip = styled.button`
  flex-shrink: 0;
  padding: 9px 18px;
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid
    ${({ $active, theme }) => ($active ? theme.color.ink : theme.color.line)};
  background: ${({ $active, theme }) =>
    $active ? theme.color.ink : "transparent"};
  color: ${({ $active, theme }) => ($active ? "#fff" : theme.color.inkSoft)};
  font-size: ${({ theme }) => theme.size.xs};
  font-weight: ${({ theme }) => theme.weight.medium};
  white-space: nowrap;
  transition: all 0.25s ${({ theme }) => theme.ease.out};

  &:hover {
    border-color: ${({ theme }) => theme.color.ink};
  }
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  justify-self: end;

  ${({ theme }) => theme.down.lg} {
    justify-self: stretch;
    justify-content: space-between;
  }
`;

const Count = styled.span`
  font-size: ${({ theme }) => theme.size.xs};
  color: ${({ theme }) => theme.color.muted};
  white-space: nowrap;
`;

const Select = styled.select`
  height: 42px;
  padding: 0 14px;
  border: 1px solid ${({ theme }) => theme.color.line};
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.color.bg};
  font-size: ${({ theme }) => theme.size.xs};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.color.brand};
  }
`;
